import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from '@mui/material';
import { designSystem } from '../theme';
import { 
  PlusCircle,
  Save,
  Send,
  Phone,
  Mic,
  CheckCircle,
  PartyPopper,
  DollarSign as MoneyIcon,
  Sparkles,
  X,
  Ban,
} from 'lucide-react';

const getStatusOptions = (theme) => [
  { value: 'new', label: 'New (Not Applied)', icon: <PlusCircle size={16} />, color: theme.palette.status.new },
  { value: 'saved', label: 'Saved for Later', icon: <Save size={16} />, color: theme.palette.status.saved },
  { value: 'applied', label: 'Applied', icon: <Send size={16} />, color: theme.palette.status.applied },
  { value: 'screening', label: 'Phone Screen', icon: <Phone size={16} />, color: theme.palette.status.screening },
  { value: 'interview', label: 'Interview Scheduled', icon: <Mic size={16} />, color: theme.palette.status.interview },
  { value: 'interviewed', label: 'Interview Complete', icon: <CheckCircle size={16} />, color: theme.palette.status.interviewed },
  { value: 'offer', label: 'Offer Received', icon: <PartyPopper size={16} />, color: theme.palette.status.offer },
  { value: 'negotiating', label: 'Negotiating', icon: <MoneyIcon size={16} />, color: theme.palette.status.negotiating },
  { value: 'accepted', label: 'Accepted', icon: <Sparkles size={16} />, color: theme.palette.status.accepted },
  { value: 'rejected', label: 'Rejected', icon: <X size={16} />, color: theme.palette.status.rejected },
  { value: 'withdrawn', label: 'Withdrawn', icon: <Ban size={16} />, color: theme.palette.status.withdrawn },
];

function JobForm({ job, onSubmit, onCancel }) {
  const theme = useTheme();
  const STATUS_OPTIONS = getStatusOptions(theme);
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    location: '',
    status: 'new',
    applied_date: '',
    interview_date: '',
    salary_offered: '',
    notes: '',
    interview_feedback: '',
    contact_name: '',
    contact_email: '',
    url: '',
    phone_script: '',
  });

  useEffect(() => {
    if (job) {
      setFormData({
        title: job.title || '',
        company: job.company || '',
        location: job.location || '',
        status: job.status || 'new',
        applied_date: job.applied_date ? job.applied_date.split('T')[0] : '',
        interview_date: job.interview_date ? job.interview_date.split('T')[0] : '',
        salary_offered: job.salary_offered || '',
        notes: job.notes || '',
        interview_feedback: job.interview_feedback || '',
        contact_name: job.contact_name || '',
        contact_email: job.contact_email || '',
        url: job.url || '',
        phone_script: job.phone_script || '',
      });
    } else {
      // Reset form when job is null (creating new job)
      setFormData({
        title: '',
        company: '',
        location: '',
        status: 'new',
        applied_date: '',
        interview_date: '',
        salary_offered: '',
        notes: '',
        interview_feedback: '',
        contact_name: '',
        contact_email: '',
        url: '',
        phone_script: '',
      });
    }
  }, [job]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // When editing via form, send all fields (backend PATCH accepts partial or full updates)
    // For inline edits (like phone script), only send changed fields
    onSubmit(formData);
  };

  const showInterviewFields = ['screening', 'interview', 'interviewed', 'offer', 'negotiating', 'accepted'].includes(formData.status);
  const showOfferFields = ['offer', 'negotiating', 'accepted'].includes(formData.status);

  return (
    <Card variant="light" sx={{ mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Typography variant="h5" fontWeight="600" sx={{ mb: 4, color: 'text.primary' }}>
            {job ? 'Update Job' : 'Add New Job'}
          </Typography>
          
          {/* Basic Info */}
          <Typography variant="h6" fontWeight="600" sx={{ mb: 2, color: 'text.primary' }}>
            Job Details
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Job Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Company"
                name="company"
                value={formData.company}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="e.g., New York, NY or Remote"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="url"
                label="Job Posting URL"
                name="url"
                value={formData.url}
                onChange={handleChange}
                placeholder="https://..."
              />
            </Grid>
          </Grid>

          {/* Status & Dates */}
          <Typography variant="h6" fontWeight="600" sx={{ mt: 4, mb: 2, color: 'text.primary' }}>
            Application Status
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Current Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  label="Current Status"
                  renderValue={(value) => {
                    const status = STATUS_OPTIONS.find(opt => opt.value === value);
                    return (
                      <Box display="flex" alignItems="center" gap={1}>
                        {status?.icon}
                        <span>{status?.label}</span>
                      </Box>
                    );
                  }}
                >
                  {STATUS_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {opt.icon}
                        <span>{opt.label}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date Applied"
                name="applied_date"
                value={formData.applied_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {showInterviewFields && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Interview Date"
                  name="interview_date"
                  value={formData.interview_date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
          </Grid>

          {/* Contact Info */}
          <Typography variant="h6" fontWeight="600" sx={{ mt: 4, mb: 2, color: 'text.primary' }}>
            Contact (Recruiter/Hiring Manager)
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Name"
                name="contact_name"
                value={formData.contact_name}
                onChange={handleChange}
                placeholder="Jane Smith"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="email"
                label="Contact Email"
                name="contact_email"
                value={formData.contact_email}
                onChange={handleChange}
                placeholder="recruiter@company.com"
              />
            </Grid>
          </Grid>

          {/* Interview Feedback */}
          {showInterviewFields && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" fontWeight="600" sx={{ mb: 2, color: 'text.primary' }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Mic size={20} />
                  Interview Feedback
                </Box>
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={5}
                label="How did the interview go? What questions were asked?"
                name="interview_feedback"
                value={formData.interview_feedback}
                onChange={handleChange}
                placeholder="Document your interview experience here. This helps track patterns and improve over time."
              />
            </Box>
          )}

          {/* Offer Details */}
          {showOfferFields && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" fontWeight="600" sx={{ mb: 2, color: 'text.primary' }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <MoneyIcon size={20} />
                  Offer Details
                </Box>
              </Typography>
              <TextField
                fullWidth
                type="number"
                label="Salary Offered ($)"
                name="salary_offered"
                value={formData.salary_offered}
                onChange={handleChange}
                placeholder="150000"
              />
            </Box>
          )}

          {/* Notes */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 2, color: 'text.primary' }}>
              Notes
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Additional notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Any additional notes about this opportunity..."
            />
          </Box>

          {/* Actions */}
          <Box 
            sx={{ 
              display: 'flex', 
              gap: 1.5, 
              mt: 4,
              pt: 3,
              borderTop: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Button 
              type="submit" 
              variant="contained" 
              size="large"
              sx={{ ...designSystem.button.contained, minWidth: 140 }}
            >
              {job ? 'Update Job' : 'Add Job'}
            </Button>
            <Button 
              variant="outlined" 
              size="large"
              onClick={onCancel}
              sx={designSystem.button.outlined}
            >
              Cancel
            </Button>
            {formData.url && (
              <Button
                variant="outlined"
                size="large"
                href={formData.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ ...designSystem.button.outlined, ml: 'auto' }}
              >
                View Posting →
              </Button>
            )}
          </Box>
        </form>
      </CardContent>
    </Card>
  );
}

export default JobForm;
